// aiChat.queries.ts — the TanStack layer over the chatbot host.
//
// ─── THERE IS NO QUERY KEY HERE, AND NOTHING TO INVALIDATE ──────────────────
//
// Asking a question is a `useMutation`, not a `useQuery`, and that is a
// correctness decision rather than a stylistic one:
//
//   - A question is a COMMAND with a side effect (the server runs generated SQL
//     and bills an LLM call). It must fire exactly when the operator presses
//     Ask, and never on a remount, a window refocus or a reconnect.
//   - Asking the same question twice must genuinely RE-ASK. Under a query key
//     the second identical ask would be served from cache, which on this
//     surface is a lie: the underlying data moves, and the whole point of a
//     re-ask is to see what the data says now.
//
// So do not go hunting for an `invalidateQueries` call — there is no cache
// entry for a reply, by design. The transcript is client state owned by the
// conversation reducer (`types.ts` / `AiConversationAction`), not by the query
// cache, precisely because a transcript is a record of what was asked and
// answered and must never be silently re-derived.
//
// `mutationKey` below exists ONLY so the devtools mutation list is legible.

import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import type { ChatReply, ChatRequest } from '../schemas/chat.schema';

import { postChat } from './aiChat.fetchers';

/**
 * Devtools label only. It is NOT an invalidation target and NOT a dedupe key —
 * TanStack does not dedupe mutations by `mutationKey`, so two overlapping asks
 * both run (which is correct; the state machine decides whether a late reply is
 * still wanted, via `askId`).
 */
export const AI_CHAT_MUTATION_KEY = ['ai', 'chat'] as const;

/**
 * What one ask needs.
 *
 * `body` carries the tenant (see `ChatRequestSchema` — `tenant_id` is in the
 * body on this host) and the trimmed `history`. `signal` is optional so a
 * caller that has no Stop affordance is not forced to invent an
 * `AbortController`.
 */
export interface AskRheoraAiVariables {
  readonly body: ChatRequest;
  readonly signal?: AbortSignal;
}

/**
 * Ask the Rheora AI assistant one question.
 *
 * `retry: false` is pinned EXPLICITLY rather than inherited. The app's shared
 * `retryPolicy` is a ceiling, not a floor — the same explicit pin appears in
 * `marketOps.queries.ts` and `bill.queries.ts` — and here an automatic retry
 * would be actively harmful twice over: it re-runs a generated SQL query and a
 * paid LLM call, and it stacks a second 25s timeout behind the first while the
 * pending bubble shows no sign that anything changed. Retrying is a user
 * gesture; `AiRetryability` decides whether it is even offered.
 *
 * Note this complements, and does not duplicate, `maxRetries: 1` on the client
 * in `aiChat.fetchers.ts`: that suppresses the transport-level retry, this
 * suppresses the React-Query-level one. Both are needed — either alone still
 * leaves one layer repeating the call.
 */
export function useAskRheoraAi(): UseMutationResult<ChatReply, unknown, AskRheoraAiVariables> {
  return useMutation<ChatReply, unknown, AskRheoraAiVariables>({
    mutationKey: AI_CHAT_MUTATION_KEY,
    mutationFn: (variables: AskRheoraAiVariables) => postChat(variables.body, variables.signal),
    retry: false,
  });
}
